(function() {
  const SECTION_SELECTOR = '[data-gift-section-wrapper]';
  const CART_CONTAINER_SELECTOR = '.form-cart';

  function initGiftSection(scope) {
    const wrapper = scope && scope.matches && scope.matches(SECTION_SELECTOR)
      ? scope
      : document.querySelector(SECTION_SELECTOR);

    if (!wrapper || wrapper.dataset.giftInitialized === 'true') return;
    wrapper.dataset.giftInitialized = 'true';

    const root = wrapper.querySelector('[data-gift-selector]');

    const isSingleGiftMode = root && root.dataset.singleGiftMode === 'true';
    const singleGiftVariantId = root ? parseInt(root.dataset.singleGiftVariantId || '0', 10) : 0;
    const singleGiftCreditCost = root ? parseInt(root.dataset.singleGiftCreditCost || '0', 10) : 0;
    const singleGiftMaxQty = root ? parseInt(root.dataset.singleGiftMaxQty || '0', 10) : 0;

    const configuredVariantIds = (wrapper.dataset.configuredVariants || '')
      .split(',')
      .map(id => parseInt(id, 10))
      .filter(Boolean);

    const liveEnabled = wrapper.dataset.liveEnabled === 'true';
    const testMode = wrapper.dataset.testMode === 'true';
    const isConsumer = wrapper.dataset.isConsumer === 'true';
    const startDate = wrapper.dataset.startDate || '';
    const endDate = wrapper.dataset.endDate || '';

    const isEditor = typeof Shopify !== 'undefined' && !!Shopify.designMode;
    const isVisualPreview = typeof Shopify !== 'undefined' && !!Shopify.visualPreviewMode;
    const isThemePreview = window.location.search.indexOf('preview_theme_id=') > -1;

    const urlParams = new URLSearchParams(window.location.search);
    const isManualTestMode = urlParams.get('gift_test') === '1';
    const isPreviewContext = isEditor || isVisualPreview || isThemePreview || isManualTestMode;

    let isApplying = false;
    let refreshTimer = null;
    let isRefreshingSection = false;

    function isWithinDateRange() {
      const today = new Date().toISOString().split('T')[0];
      if (startDate && today < startDate) return false;
      if (endDate && today > endDate) return false;
      return true;
    }

    function shouldAllowFeature() {
      if (!isConsumer) return false;
      if (!liveEnabled) return testMode && isPreviewContext;
      if (!isWithinDateRange()) return testMode && isPreviewContext;
      if (testMode) return isPreviewContext;
      return true;
    }

    async function getCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Cart ophalen mislukt');
      return response.json();
    }

    async function changeLine(line, quantity) {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ line, quantity })
      });

      if (!response.ok) {
        throw new Error('Cartregel wijzigen mislukt');
      }
    }

    function getGiftLines(cart) {
      return cart.items
        .map((item, index) => ({ ...item, line: index + 1 }))
        .filter(item => {
          if (!item.properties || item.properties._free_gift !== 'true') return false;
          return configuredVariantIds.includes(Number(item.id));
        });
    }

    function getPaidItems(cart) {
      return cart.items.filter(item => !item.properties || item.properties._free_gift !== 'true');
    }

    function getAvailableCreditsFromCart(cart) {
      const baseThreshold = root ? parseInt(root.dataset.baseThreshold || '0', 10) : 0;
      const minItems = root ? parseInt(root.dataset.minItems || '0', 10) : 0;
      if (!baseThreshold) return 0;

      const paidItems = getPaidItems(cart);
      const paidItemCount = paidItems.reduce((sum, item) => sum + item.quantity, 0);
      if (paidItemCount < minItems) return 0;

      const paidSubtotal = paidItems.reduce((sum, item) => sum + item.final_line_price, 0);
      return Math.floor(paidSubtotal / baseThreshold);
    }

    function getUsedCreditsFromGiftLines(lines) {
      return lines.reduce((sum, line) => {
        const cost = parseInt((line.properties && line.properties._gift_credit_cost) || '0', 10);
        if (cost > 0) return sum + (cost * line.quantity);

        if (root) {
          const matchingCard = root.querySelector(`[data-gift-card][data-variant-id="${line.id}"]`);
          if (matchingCard) {
            const fallbackCost = parseInt(matchingCard.dataset.creditCost || '0', 10);
            return sum + (fallbackCost * line.quantity);
          }
        }

        return sum;
      }, 0);
    }

    function buildCartSignature(cart) {
      return cart.items
        .map(item => {
          const props = item.properties ? JSON.stringify(item.properties) : '';
          return `${item.key}:${item.quantity}:${props}`;
        })
        .sort()
        .join('|');
    }

    async function getCurrentCartSignature() {
      const cart = await getCart();
      return buildCartSignature(cart);
    }

    async function removeConfiguredGiftLines(lines) {
      if (!lines.length) return false;

      for (const line of lines) {
        await changeLine(line.line, 0);
      }

      return true;
    }

    async function refreshSectionUI() {
      if (isRefreshingSection) return;
      const sectionId = wrapper.dataset.sectionId;
      if (!sectionId) return;

      isRefreshingSection = true;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);

        const response = await fetch(url.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error('Section refresh mislukt');

        const html = await response.text();
        const temp = document.createElement('div');
        temp.innerHTML = html;

        const newWrapper = temp.querySelector(SECTION_SELECTOR);
        if (!newWrapper) return;

        wrapper.replaceWith(newWrapper);
        initGiftSection(newWrapper);
      } catch (error) {
        console.error('[Gift selector] Section refresh error', error);
      } finally {
        isRefreshingSection = false;
      }
    }

    async function validateGiftCart() {
      if (!shouldAllowFeature()) {
        const cart = await getCart();
        const giftLines = getGiftLines(cart);
        if (giftLines.length) {
          await removeConfiguredGiftLines(giftLines);
          return { changed: true, removedInvalidGift: true };
        }
        return { changed: false, removedInvalidGift: false };
      }

      if (!root) return { changed: false, removedInvalidGift: false };

      if (isSingleGiftMode) {
        const changed = await syncSingleGiftAutomatically();
        return {
          changed,
          removedInvalidGift: false
        };
      }

      const cart = await getCart();
      const giftLines = getGiftLines(cart);
      const availableCredits = getAvailableCreditsFromCart(cart);
      const usedCredits = getUsedCreditsFromGiftLines(giftLines);

      const shouldRemoveAll = usedCredits > availableCredits;

      if (shouldRemoveAll && giftLines.length) {
        await removeConfiguredGiftLines(giftLines);
        return { changed: true, removedInvalidGift: true };
      }

      return {
        changed: false,
        removedInvalidGift: false,
        cart,
        availableCredits,
        usedCredits
      };
    }

    if (!shouldAllowFeature()) {
      if (root) {
        root.style.display = 'none';
      }

      if (configuredVariantIds.length) {
        getCart()
          .then(cart => removeConfiguredGiftLines(getGiftLines(cart)))
          .then(removed => {
            if (removed) window.location.reload();
          })
          .catch(error => {
            console.error('[Gift selector] Cleanup error', error);
          });
      }

      return;
    }

    if (!root) return;

    const cards = Array.from(root.querySelectorAll('[data-gift-card]'));
    const statusEl = root.querySelector('[data-gift-status]');
    const usedCreditsEl = root.querySelector('[data-used-credits]');
    const remainingCreditsEl = root.querySelector('[data-remaining-credits]');
    const availableCreditsEl = root.querySelector('[data-available-credits]');
    const testNote = root.querySelector('[data-gift-test-note]');
    const groupTitleEl = root.querySelector('[data-gift-group-title]');
    const groupSubEl = root.querySelector('[data-gift-group-sub]');
    const mainHeadingEl = root.querySelector('[data-gift-main-heading]');
    const defaultHeading = mainHeadingEl ? mainHeadingEl.textContent : '';
    const statsEl = root.querySelector('[data-gift-stats]');
    const groupHeadEl = root.querySelector('[data-gift-group-head]');
    const autoGiftCardEl = root.querySelector('[data-auto-gift-card]');

    function updateSingleGiftModeUI() {
      if (!isSingleGiftMode) return;

      if (statsEl) statsEl.style.display = 'none';

      if (groupHeadEl) {
        const titleEl = groupHeadEl.querySelector('.cart-gift-group__title');
        const subEl = groupHeadEl.querySelector('.cart-gift-group__sub');

        if (titleEl) {
          titleEl.textContent = 'Uw gratis cadeau';
        }

        if (subEl) {
          subEl.textContent = 'Dit cadeau wordt automatisch toegevoegd zodra u voldoende credits hebt.';
        }
      }
    }

    if (testMode && isPreviewContext && testNote) {
      testNote.classList.remove('hidden');
    }

    updateSingleGiftModeUI();

    function getSelections() {
      return cards.map(card => {
        const qtyInput = card.querySelector('[data-gift-qty]');
        return {
          card,
          variantId: parseInt(card.dataset.variantId || '0', 10),
          creditCost: parseInt(card.dataset.creditCost || '0', 10),
          qty: parseInt(qtyInput && qtyInput.value ? qtyInput.value : '0', 10) || 0
        };
      });
    }

    async function syncSingleGiftAutomatically() {
      if (!isSingleGiftMode || !singleGiftVariantId || !singleGiftCreditCost) return false;

      const cart = await getCart();
      const giftLines = getGiftLines(cart).filter(line => Number(line.id) === singleGiftVariantId);
      const availableCredits = getAvailableCreditsFromCart(cart);
      let wantedQty = Math.floor(availableCredits / singleGiftCreditCost);

      if (singleGiftMaxQty > 0) {
        wantedQty = Math.min(wantedQty, singleGiftMaxQty);
      }

      let changed = false;

      for (const line of giftLines) {
        if (line.quantity !== wantedQty) {
          await changeLine(line.line, 0);
          changed = true;
        }
      }

      if (wantedQty > 0) {
        const hasExactQty = giftLines.some(line => line.quantity === wantedQty);

        if (!hasExactQty) {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: singleGiftVariantId,
                quantity: wantedQty,
                properties: {
                  _free_gift: 'true',
                  _gift_source: 'cart_gift_selector_auto',
                  _gift_credit_cost: String(singleGiftCreditCost)
                }
              }]
            })
          });

          if (!response.ok) {
            throw new Error('Automatisch cadeau toevoegen mislukt');
          }

          changed = true;
        }
      }

      return changed;
    }

    function setStatus(message, type) {
      if (!statusEl) return;

      statusEl.textContent = message || '';
      statusEl.classList.remove('is-error', 'is-success', 'is-muted');

      if (type === 'error') {
        statusEl.classList.add('is-error');
      } else if (type === 'success') {
        statusEl.classList.add('is-success');
      } else {
        statusEl.classList.add('is-muted');
      }
    }

    async function autoAddSingleGiftIfNeeded() {
      if (!root || isApplying) return false;

      const availableCards = cards.filter(card => card.dataset.isLocked !== 'true');
      const allCards = cards.filter(card => {
        const variantId = parseInt(card.dataset.variantId || '0', 10);
        return !!variantId;
      });

      if (allCards.length !== 1) return false;

      const card = allCards[0];
      const variantId = parseInt(card.dataset.variantId || '0', 10);
      const creditCost = parseInt(card.dataset.creditCost || '0', 10);

      if (!variantId || !creditCost) return false;

      const cart = await getCart();
      const availableCredits = getAvailableCreditsFromCart(cart);

      let maxQty = Math.floor(availableCredits / creditCost);

      if (singleGiftMaxQty > 0) {
        maxQty = Math.min(maxQty, singleGiftMaxQty);
      }

      const existingGiftLines = getGiftLines(cart).filter(line => Number(line.id) === variantId);
      const currentQtyInCart = existingGiftLines.reduce((sum, line) => sum + line.quantity, 0);

      if (currentQtyInCart === maxQty) return false;

      isApplying = true;

      try {
        for (const line of existingGiftLines) {
          await changeLine(line.line, 0);
        }

        if (maxQty > 0) {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: maxQty,
                properties: {
                  _free_gift: 'true',
                  _gift_source: 'cart_gift_selector_auto',
                  _gift_credit_cost: String(creditCost)
                }
              }]
            })
          });

          if (!response.ok) {
            throw new Error('Automatisch cadeau toevoegen mislukt');
          }
        }

        return true;
      } catch (error) {
        console.error('[Gift selector] Auto add single gift error', error);
        return false;
      } finally {
        isApplying = false;
      }
    }

    async function updateCreditUI() {
      try {
        const cart = await getCart();
        const availableCredits = getAvailableCreditsFromCart(cart);
        const giftLines = getGiftLines(cart);
        const usedCreditsInCart = getUsedCreditsFromGiftLines(giftLines);
        const remainingCreditsInCart = Math.max(0, availableCredits - usedCreditsInCart);

        if (availableCreditsEl) availableCreditsEl.textContent = availableCredits;
        if (usedCreditsEl) usedCreditsEl.textContent = usedCreditsInCart;
        if (remainingCreditsEl) remainingCreditsEl.textContent = remainingCreditsInCart;

        const selections = getSelections();

        cards.forEach(card => {
          const input = card.querySelector('[data-gift-qty]');
          const plusBtn = card.querySelector('[data-gift-plus]');
          const minusBtn = card.querySelector('[data-gift-minus]');
          const applyBtn = card.querySelector('[data-gift-apply-single]');
          const variantId = parseInt(card.dataset.variantId || '0', 10);
          const cost = parseInt(card.dataset.creditCost || '0', 10);
          const currentQty = parseInt(input && input.value ? input.value : '0', 10) || 0;
          const inCartQty = parseInt(card.dataset.inCartQty || '0', 10);
          const lockedBecauseInsufficientCredits = availableCredits < cost;

          const previewUsedWithoutThis = selections.reduce((sum, item) => {
            if (item.variantId === variantId) return sum;
            return sum + (item.creditCost * item.qty);
          }, 0);

          const canAddMore = !lockedBecauseInsufficientCredits
            && (previewUsedWithoutThis + ((currentQty + 1) * cost)) <= availableCredits;

          card.classList.toggle('is-locked', lockedBecauseInsufficientCredits);
          card.dataset.isLocked = lockedBecauseInsufficientCredits ? 'true' : 'false';

          if (input) input.disabled = lockedBecauseInsufficientCredits;
          if (plusBtn) plusBtn.disabled = !canAddMore;
          if (minusBtn) minusBtn.disabled = lockedBecauseInsufficientCredits || currentQty <= 0;

          if (applyBtn) {
            const previewTotalWithThis = previewUsedWithoutThis + (currentQty * cost);
            const isValid = previewTotalWithThis <= availableCredits && !lockedBecauseInsufficientCredits;

            applyBtn.disabled = !isValid;

            if (lockedBecauseInsufficientCredits) {
              const missingCredits = cost - availableCredits;
              applyBtn.textContent = `Nog ${missingCredits} credit${missingCredits !== 1 ? 's' : ''} nodig`;
            } else if (inCartQty > 0) {
              applyBtn.textContent = 'Aantal bijwerken';
            } else {
              applyBtn.textContent = 'Toevoegen';
            }
          }
        });

        const previewUsedCredits = selections.reduce((sum, item) => sum + (item.creditCost * item.qty), 0);

        if (previewUsedCredits > availableCredits) {
          setStatus('U heeft meer credits geselecteerd dan beschikbaar.', 'error');
        } else {
          setStatus('', 'muted');
        }
      } catch (error) {
        console.error('[Gift selector] Credit UI error', error);
      }
    }

    async function updateSingleGiftInCart(card) {
      if (!card || isApplying) return;

      const input = card.querySelector('[data-gift-qty]');
      const applyBtn = card.querySelector('[data-gift-apply-single]');
      const variantId = parseInt(card.dataset.variantId || '0', 10);
      const creditCost = parseInt(card.dataset.creditCost || '0', 10);
      const wantedQty = parseInt(input && input.value ? input.value : '0', 10) || 0;

      const cartBefore = await getCart();
      const availableCredits = getAvailableCreditsFromCart(cartBefore);
      const selections = getSelections();
      const usedCredits = selections.reduce((sum, item) => sum + (item.creditCost * item.qty), 0);

      if (usedCredits > availableCredits) {
        setStatus('U heeft meer credits geselecteerd dan beschikbaar.', 'error');
        return;
      }

      isApplying = true;

      if (applyBtn) {
        applyBtn.disabled = true;
        applyBtn.classList.add('is-loading');
      }

      setStatus('Cadeau wordt bijgewerkt...', 'muted');

      try {
        const existingGiftLines = getGiftLines(cartBefore).filter(line => Number(line.id) === variantId);

        for (const line of existingGiftLines) {
          await changeLine(line.line, 0);
        }

        if (wantedQty > 0) {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: wantedQty,
                properties: {
                  _free_gift: 'true',
                  _gift_source: 'cart_gift_selector',
                  _gift_credit_cost: String(creditCost)
                }
              }]
            })
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('[Gift selector] Add error response:', errorText);
            throw new Error('Gratis cadeau toevoegen mislukt');
          }
        }

        setStatus('Cadeau bijgewerkt.', 'success');

        await refreshSectionUI();

        window.location.reload();
        return;
      } catch (error) {
        console.error('[Gift selector] Single update error', error);
        setStatus('Er ging iets mis bij het bijwerken van het cadeau.', 'error');
      } finally {
        isApplying = false;

        if (applyBtn) {
          applyBtn.disabled = false;
          applyBtn.classList.remove('is-loading');
        }
      }
    }

    async function validateAndRefreshIfNeeded(forceRefresh) {
      try {
        const currentSignature = await getCurrentCartSignature();
        const previousSignature = window.__ppGiftLastCartSignature || '';

        if (!forceRefresh && currentSignature === previousSignature) {
          return false;
        }

        const validation = await validateGiftCart();
        const newSignature = await getCurrentCartSignature();
        window.__ppGiftLastCartSignature = newSignature;

        if (validation.removedInvalidGift) {
          window.location.reload();
          return true;
        }

        if (validation.changed) {
          window.location.reload();
          return true;
        }

        const autoAdded = await autoAddSingleGiftIfNeeded();
        if (autoAdded) {
          window.location.reload();
          return true;
        }

        await refreshSectionUI();
        return true;
      } catch (error) {
        console.error('[Gift selector] Validation error', error);
        return false;
      }
    }

    function queueValidateAndRefresh(delay) {
      window.clearTimeout(refreshTimer);

      if (window.__ppGiftPollingInterval) {
        window.clearInterval(window.__ppGiftPollingInterval);
        window.__ppGiftPollingInterval = null;
      }

      refreshTimer = window.setTimeout(async function() {
        let attempts = 0;
        const maxAttempts = 12;
        const initialSignature = window.__ppGiftLastCartSignature || '';

        window.__ppGiftPollingInterval = window.setInterval(async function() {
          if (window.__ppGiftRefreshBusy) return;

          window.__ppGiftRefreshBusy = true;

          try {
            const currentSignature = await getCurrentCartSignature();

            if (currentSignature !== initialSignature) {
              const refreshed = await validateAndRefreshIfNeeded(true);

              if (refreshed) {
                window.clearInterval(window.__ppGiftPollingInterval);
                window.__ppGiftPollingInterval = null;
              }
            }
          } catch (error) {
            console.error('[Gift selector] Polling error', error);
          } finally {
            window.__ppGiftRefreshBusy = false;
          }

          attempts += 1;

          if (attempts >= maxAttempts) {
            window.clearInterval(window.__ppGiftPollingInterval);
            window.__ppGiftPollingInterval = null;
          }
        }, 350);
      }, delay || 300);
    }

    window.__ppGiftQueueRefresh = function(delay) {
      queueValidateAndRefresh(delay);
    };

    cards.forEach(card => {
      const minusBtn = card.querySelector('[data-gift-minus]');
      const plusBtn = card.querySelector('[data-gift-plus]');
      const input = card.querySelector('[data-gift-qty]');
      const cost = parseInt(card.dataset.creditCost || '0', 10);

      if (minusBtn && input) {
        minusBtn.addEventListener('click', function() {
          const current = parseInt(input.value || '0', 10) || 0;
          input.value = Math.max(0, current - 1);
          updateCreditUI();
        });
      }

      if (plusBtn && input) {
        plusBtn.addEventListener('click', async function() {
          const cart = await getCart();
          const availableCredits = getAvailableCreditsFromCart(cart);
          const current = parseInt(input.value || '0', 10) || 0;
          const nextUsedCredits = getSelections().reduce((sum, item) => sum + (item.creditCost * item.qty), 0) + cost;

          if (nextUsedCredits <= availableCredits) {
            input.value = current + 1;
            updateCreditUI();
          }
        });
      }

      if (input) {
        input.addEventListener('input', function() {
          let value = parseInt(input.value || '0', 10);
          if (isNaN(value) || value < 0) value = 0;
          input.value = value;
          updateCreditUI();
        });

        input.addEventListener('change', function() {
          let value = parseInt(input.value || '0', 10);
          if (isNaN(value) || value < 0) value = 0;
          input.value = value;
          updateCreditUI();
        });
      }
    });

    if (!window.__ppGiftApplyEventsBound) {
      window.__ppGiftApplyEventsBound = true;

      document.addEventListener('click', function(event) {
        const button = event.target.closest('[data-gift-apply-single]');
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const card = button.closest('[data-gift-card]');
        if (!card) return;

        updateSingleGiftInCart(card);
      });
    }

    if (!window.__ppGiftGlobalEventsBound) {
      window.__ppGiftGlobalEventsBound = true;

      document.addEventListener('change', function(event) {
        const target = event.target;
        if (!target) return;

        if (
          target.matches('input[name="updates[]"]') ||
          target.matches('.disable-on-change') ||
          target.matches('[data-line]') ||
          target.matches('input.qty-input') ||
          target.matches('input[type="number"]') ||
          target.closest('.input-amount') ||
          target.closest('.quantity') ||
          target.closest('.cart-item')
        ) {
          if (typeof window.__ppGiftQueueRefresh === 'function') {
            window.__ppGiftQueueRefresh(500);
          }
        }
      });

      document.addEventListener('click', function(event) {
        const target = event.target.closest('a, button');
        if (!target) return;

        if (target.closest('[data-gift-card]')) return;
        if (target.matches('[data-gift-apply-single]')) return;

        if (
          target.matches('.remove-from-cart-link') ||
          target.matches('[name="minus"]') ||
          target.matches('[name="plus"]') ||
          target.closest('.input-amount') ||
          target.closest('.quantity') ||
          target.closest('.cart-item')
        ) {
          if (typeof window.__ppGiftQueueRefresh === 'function') {
            window.__ppGiftQueueRefresh(600);
          }
        }
      });
    }

    const cartContainer = document.querySelector(CART_CONTAINER_SELECTOR);
    if (cartContainer) {
      if (window.__ppGiftCartObserver) {
        window.__ppGiftCartObserver.disconnect();
      }

      window.__ppGiftCartObserver = new MutationObserver(function() {
        queueValidateAndRefresh(250);
      });

      window.__ppGiftCartObserver.observe(cartContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: false
      });
    }

    getCurrentCartSignature()
      .then(function(signature) {
        window.__ppGiftLastCartSignature = signature;
        return validateGiftCart();
      })
      .then(async function(validation) {
        const newSignature = await getCurrentCartSignature();
        window.__ppGiftLastCartSignature = newSignature;

        if (validation && (validation.removedInvalidGift || validation.changed)) {
          window.location.reload();
          return;
        }

        const autoAdded = await autoAddSingleGiftIfNeeded();
        if (autoAdded) {
          window.location.reload();
          return;
        }

        return updateCreditUI();
      })
      .catch(function(error) {
        console.error('[Gift selector] Initial validation error', error);
      });
  }

  window.initGiftSection = initGiftSection;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initGiftSection(document.querySelector(SECTION_SELECTOR));
    });
  } else {
    initGiftSection(document.querySelector(SECTION_SELECTOR));
  }
})();
